import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import {
  SubscriptionPackage,
  SubscriptionPackageDraft,
  useCreateSubscriptionPackage,
  useDeleteSubscriptionPackage,
  useSubscriptionPackages,
  useUpdateSubscriptionPackage,
} from '../../queries/subscriptionPackages';
import { useTags } from '../../queries/tags';
import { SubscriptionPackageFormDialog } from './SubscriptionPackageFormDialog';
import { saleStatus } from './sale-status';
import { useToast } from '../../contexts/ToastContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { Checkbox } from '../../components/ui/Checkbox';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

const EMPTY_DRAFT: SubscriptionPackageDraft = {
  name: '',
  package_code: '',
  description: '',
  billing_model: 'subscription',
  is_active: true,
  is_private: false,
  is_default_for_all_users: false,
  auto_assign_on_registration: false,
};

const CURRENCY = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

function formatPrice(pkg: SubscriptionPackage): string {
  if (pkg.billing_model === 'one_time') {
    return pkg.price_one_time != null ? `${CURRENCY.format(pkg.price_one_time)} once` : '—';
  }
  const weekly = pkg.price_weekly != null ? `${CURRENCY.format(pkg.price_weekly)}/wk` : null;
  const monthly = pkg.price_monthly != null ? `${CURRENCY.format(pkg.price_monthly)}/mo` : null;
  return [weekly, monthly].filter(Boolean).join(' · ') || '—';
}

export function SubscriptionPackagesPage() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data: packages = [], isLoading } = useSubscriptionPackages(includeInactive);
  const { data: tags = [] } = useTags();
  const create = useCreateSubscriptionPackage();
  const update = useUpdateSubscriptionPackage();
  const remove = useDeleteSubscriptionPackage();
  const { success, error } = useToast();

  const [editing, setEditing] = useState<SubscriptionPackageDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<SubscriptionPackage | null>(null);

  const saving = create.isPending || update.isPending;

  const handleSave = () => {
    if (!editing) return;
    setFormError(null);
    const isEdit = !!editing.id;
    const onSettled = {
      onSuccess: () => {
        setEditing(null);
        success(isEdit ? 'Package updated.' : 'Package created.');
      },
      onError: (err: unknown) => setFormError(extractErrorMessage(err, 'Save failed.')),
    };
    if (editing.id) {
      update.mutate({ ...editing, id: editing.id }, onSettled);
    } else {
      create.mutate(editing, onSettled);
    }
  };

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        success(`Deactivated "${deleting.name}".`);
        setDeleting(null);
      },
      onError: (err: unknown) => {
        error(extractErrorMessage(err, 'Delete failed.'));
        setDeleting(null);
      },
    });
  };

  const columns: DataTableColumn<SubscriptionPackage>[] = [
    {
      key: 'name',
      header: 'Name',
      priority: true,
      render: (pkg) => {
        const sale = saleStatus(pkg);
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5 font-medium">
            {pkg.name}
            {pkg.is_default_for_all_users && <Badge variant="outline">default</Badge>}
            {sale && (
              <Badge variant={sale.variant} title="Melbourne time">
                {sale.label}
              </Badge>
            )}
          </span>
        );
      },
    },
    { key: 'code', header: 'Code', priority: false, render: (pkg) => <span className="font-mono text-xs text-muted-foreground">{pkg.package_code}</span> },
    { key: 'price', header: 'Price', priority: true, render: (pkg) => <span className="text-muted-foreground @2xl:text-foreground">{formatPrice(pkg)}</span> },
    { key: 'subs', header: 'Active subs', priority: false, alignEnd: true, render: (pkg) => <span className="tabular-nums">{pkg.active_subscriptions}</span> },
    { key: 'private', header: 'Private', priority: false, render: (pkg) => (pkg.is_private ? <Badge variant="warning">Private</Badge> : <span className="text-muted-foreground">—</span>) },
    { key: 'active', header: 'Active', priority: false, render: (pkg) => (pkg.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="outline">Inactive</Badge>) },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (pkg) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => setEditing({ ...pkg, tag_rules: [...pkg.tag_rules] })} title="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleting(pkg)}
            disabled={pkg.active_subscriptions > 0}
            className="text-muted-foreground hover:text-destructive"
            title={pkg.active_subscriptions > 0 ? 'Cannot deactivate: has active subscriptions' : 'Deactivate'}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Subscription Packages"
        description="The catalogue of plans available to customers."
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              Show inactive
            </label>
            <Button onClick={() => setEditing({ ...EMPTY_DRAFT })}>
              <Plus className="h-4 w-4" />
              Add package
            </Button>
          </>
        }
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable columns={columns} data={packages} getRowKey={(pkg) => String(pkg.id)} emptyMessage="No packages yet." />
      )}

      {editing && (
        <SubscriptionPackageFormDialog
          draft={editing}
          onChange={setEditing}
          onCancel={() => {
            setEditing(null);
            setFormError(null);
          }}
          onSave={handleSave}
          saving={saving}
          error={formError}
          allTags={tags}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Deactivate package?"
        description={`"${deleting?.name}" will be hidden from customers and can no longer be subscribed to. Existing subscriptions are unaffected. You can reactivate it later by editing it.`}
        confirmLabel="Deactivate"
        destructive
        busy={remove.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
